/**
 * Componente AutomationKanbanColumn - Coluna do Kanban de Automações
 * Exibe automações (gatilhos e ações) vinculadas a uma fase específica
 * 
 * RELEASE: 1.0.0 - 2026-01-26 - Componente inicial para visualização de automações
 */
import React from 'react';
import { Plus, Zap, Play, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { AutomationCard } from './AutomationCard';
import { PhaseAutomation } from '@/hooks/usePhaseAutomations';

/**
 * Converte cor hex para rgba com opacidade
 */
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Props do componente
interface AutomationKanbanColumnProps {
  /** ID da fase */
  phaseId: string;
  /** Nome da fase */
  phaseName: string;
  /** Cor da fase (hex) */
  phaseColor: string | null;
  /** Automações que disparam quando deal entra nesta fase */
  triggerAutomations: PhaseAutomation[];
  /** Automações que movem deals PARA esta fase (futuro) */
  actionAutomations: PhaseAutomation[];
  /** Callback para criar novo fluxo pré-configurado para esta fase */
  onCreateFlow?: (phaseId: string) => void;
  /** Callback para editar um fluxo existente */
  onEditFlow?: (flowId: string) => void;
  /** Indica se está carregando */
  isLoading?: boolean;
}

/**
 * Coluna do Kanban de Automações
 * Layout inspirado no Bitrix24: Header da fase + Seção Gatilhos + Seção Ações + Botão Criar
 */
export function AutomationKanbanColumn({
  phaseId,
  phaseName,
  phaseColor,
  triggerAutomations,
  actionAutomations,
  onCreateFlow,
  onEditFlow,
  isLoading = false,
}: AutomationKanbanColumnProps) {
  // Total de automações na coluna
  const totalAutomations = triggerAutomations.length + actionAutomations.length;
  
  // Conta automações ativas
  const activeCount = [...triggerAutomations, ...actionAutomations].filter(
    a => a.status === 'active'
  ).length;

  return (
    <div
      className={cn(
        'flex flex-col w-[260px] h-full shrink-0 bg-muted/30 rounded-lg overflow-hidden',
        'border border-border/50'
      )}
    >
      {/* Header da coluna - Com fundo colorido sutil */}
      <div 
        className="p-3 border-b border-border/50 rounded-t-lg"
        style={{ backgroundColor: hexToRgba(phaseColor || '#6b7280', 0.15) }}
      >
        {/* Nome da fase com indicador de cor */}
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: phaseColor || '#6b7280' }}
          />
          <h3 className="font-medium text-sm truncate flex-1">
            {phaseName}
          </h3>
          
          {/* Badge com contagem de automações */}
          {totalAutomations > 0 && (
            <Badge 
              variant="secondary" 
              className={cn(
                'h-5 px-1.5 text-[10px] gap-1',
                activeCount > 0 && 'bg-amber-500/10 text-amber-600 border-amber-200'
              )}
            >
              <Zap className="h-3 w-3" />
              {totalAutomations}
            </Badge>
          )}
        </div>
      </div>

      {/* Área de conteúdo com scroll */}
      <ScrollArea className="flex-1 h-0 min-h-0">
        <div className="p-3 space-y-4 w-full min-w-0">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Seção Gatilhos (flows que disparam quando deal entra na fase) */}
          {!isLoading && triggerAutomations.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <Play className="h-3 w-3" />
                Gatilhos
              </div>
              <div className="space-y-2 overflow-hidden">
                {triggerAutomations.map((automation) => (
                  <AutomationCard
                    key={automation.id}
                    automation={automation}
                    onEdit={onEditFlow}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Seção Ações (flows que movem deals PARA esta fase) - Futuro */}
          {!isLoading && actionAutomations.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <Zap className="h-3 w-3" />
                Ações
              </div>
              <div className="space-y-2 overflow-hidden">
                {actionAutomations.map((automation) => (
                  <AutomationCard
                    key={automation.id}
                    automation={automation}
                    onEdit={onEditFlow}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty state quando não tem automações */}
          {!isLoading && totalAutomations === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Zap className="h-8 w-8 mb-2 opacity-30" />
              <p className="text-xs text-center">
                Nenhuma automação<br />configurada
              </p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Footer: Botão para criar nova automação */}
      <div className="p-3 border-t border-border/50">
        <Button
          variant="ghost"
          size="sm"
          className="w-full h-8 text-xs gap-1.5"
          onClick={() => onCreateFlow?.(phaseId)}
        >
          <Plus className="h-3.5 w-3.5" />
          Criar Automação
        </Button>
      </div>
    </div>
  );
}
